import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  AttachMoney as AttachMoneyIcon,
  Analytics as AnalyticsIcon,
  Map as MapIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { brandColors } from '../../theme';
import { HeroSection } from '../../components/marketing/HeroSection';
import { Testimonial } from '../../components/marketing/Testimonial';
import { SEO } from '../../components/SEO';
import { generateBreadcrumbSchema } from '../../components/SEO/schemas';
import { FAQSection } from '../../components/FAQ/FAQSection';
import { useContinents } from '../../hooks/useMarketCompassApi';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import 'flag-icons/css/flag-icons.min.css';

const createSlug = (name: string): string => {
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const getContinentImagePath = (continentName: string): string => {
  const imageName = createSlug(continentName);
  return `/images/continents/${imageName}.png`;
};

interface PopularCountry {
  country: string;
  continent: string;
  code: string;
}

const popularCountries: PopularCountry[] = [
  { country: 'United States', continent: 'North America', code: 'us' },
  { country: 'Costa Rica', continent: 'North America', code: 'cr' },
  { country: 'Mexico', continent: 'North America', code: 'mx' },
  { country: 'Cayman Islands', continent: 'North America', code: 'ky' },
  { country: 'Turks & Caicos', continent: 'North America', code: 'tc' },
  { country: 'Bermuda', continent: 'North America', code: 'bm' },
];

const CustomCompSetsPage: React.FC = () => {
  const { navigateTo } = useAppNavigation();
  const { hasStorePrivileged } = useUserPermissions();
  
  // Breadcrumb schema for SEO
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Custom Comp Sets', url: '/custom-comp-sets' }
  ]);

  const { data: continents = [], isLoading: loadingContinents, error: continentsError } = useContinents();

  const handleContinentClick = (continentName: string) => {
    const continentSlug = createSlug(continentName);
    navigateTo(`/global-market-reports/${continentSlug}`);
  };

  const handleCountryClick = (country: PopularCountry) => {
    const continentSlug = createSlug(country.continent);
    const countrySlug = country.country === 'UK' ? 'united-kingdom' : createSlug(country.country);
    navigateTo(`/global-market-reports/${continentSlug}/${countrySlug}`);
  };

  const whatsInsideItems = [
    {
      title: 'Core KPIs',
      description: 'Occupancy, ADR, RevPAR tracked monthly, quarterly, and annually.',
      icon: <TrendingUpIcon />,
    },
    {
      title: 'Supply & Demand',
      description: 'Rooms available, rooms sold, and revenue by class and comp set.',
      icon: <AnalyticsIcon />,
    },
    {
      title: 'Market Performance Indexes',
      description: 'Comparative performance against local, regional, and national averages.',
      icon: <AssessmentIcon />,
    },
    {
      title: 'Class-Level Detail',
      description: 'Luxury, Upper Upscale, Upscale, Upper Midscale, Midscale, and Economy breakdowns.',
      icon: <BusinessIcon />,
    },
    {
      title: 'Comp Set Profiles',
      description: 'Detailed review of selected hotels, brand mix, meeting space, and demand generators.',
      icon: <MapIcon />,
    },
    {
      title: 'Historical Trends',
      description: '10+ years of performance history, highlighting cyclical and structural changes.',
      icon: <PsychologyIcon />,
    },
  ];

  const applications = [
    {
      title: 'Feasibility & Development',
      description: 'Confirm demand and identify gaps before breaking ground.',
      icon: <BusinessIcon />,
    },
    {
      title: 'Valuation & Appraisal',
      description: 'Substantiate assumptions with defensible third-party data.',
      icon: <AttachMoneyIcon />,
    },
    {
      title: 'Investment & Acquisition',
      description: 'Validate underwriting and test market positioning.',
      icon: <TrendingUpIcon />,
    },
    {
      title: 'Strategic Planning',
      description: 'Equip ownership and operators with performance benchmarks.',
      icon: <PsychologyIcon />,
    },
  ];

  const whyChooseReasons = [
    {
      title: 'Depth of Data',
      description: 'Every performance measure you need, not a partial snapshot.',
      icon: <AnalyticsIcon />,
    },
    {
      title: 'Ease of Access',
      description: 'Packaged in a clean, visual, narrative-driven 20+ page report.',
      icon: <SpeedIcon />,
    },
    {
      title: 'Affordability',
      description: 'A fraction of the cost of other market data products, while delivering the same or more information.',
      icon: <AttachMoneyIcon />,
    },
    {
      title: 'Expert Interpretation',
      description: 'Horwath HTL brings decades of valuation and advisory expertise to ensure the numbers are explained, not just presented.',
      icon: <StarIcon />,
    },
  ];

  if (loadingContinents) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading markets...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (continentsError) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ my: 4 }}>
          Failed to load market data. Please refresh the page.
        </Alert>
      </Container>
    );
  }

  // Create continent data matching the dashboard format
  // Filter out inactive continents for users without store.privileged permission
  const continentData = continents
    .filter(continent => hasStorePrivileged || continent.isActive)
    .map(continent => ({
      name: continent.name,
      count: `${continent.activeMarketCount}+`,
      isActive: continent.isActive,
    }));

  // Check if there are any inactive continents being displayed
  const hasInactiveContinents = continentData.some(c => !c.isActive);

  return (
    <>
      <SEO
        title="Custom Comp Set Reports - Hotel Market Analysis"
        description="Create custom competitive sets for hotel market analysis. Compare performance metrics across selected properties and markets with tailored benchmarking reports."
        canonical="/custom-comp-sets"
        structuredData={breadcrumbSchema}
      />
      <HeroSection 
        showExploreButton={false} 
        showSampleButton={false} 
        align="top" 
        textAlign="left" 
        showDivider={false} 
        showSubheading={false}
        title="Custom Comp Set Reports"
        description="The Custom Comp Set Report by MarketCompass, powered by Horwath HTL, is a comprehensive market intelligence solution designed for the hospitality industry. Packaged as a 20+ page professional report, it brings together all of the performance insights you would expect in a full market trend study—delivered in a streamlined, cost-effective format. These reports include the complete suite of hotel performance metrics, with long-term historical context, competitive set analysis, and class-level breakouts, giving you everything you need to benchmark a property, test market feasibility, or support an appraisal. In essence, it's a full market and comp set diagnostic—without the high price tag often associated with this level of detail."
      />

      {/* Continents and Popular Countries Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: brandColors.white }}>
        <Container maxWidth="lg">
          {/* Show warning when inactive continents are displayed with special access */}
          {hasInactiveContinents && hasStorePrivileged && (
            <Alert severity="warning" sx={{ mb: 4 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                Special Access View
              </Typography>
              <Typography variant="body2">
                Some continents shown are flagged as Internal Only which means they are only visible to Horwath HTL employees and not regular users.
              </Typography>
            </Alert>
          )}
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: brandColors.indigo,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Navigate to Your Market of Interest
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: brandColors.mediumGray,
                fontSize: '1.125rem',
                mb: 2,
              }}
            >
              Select a market below to purchase a custom comp set report tailored to your specific needs
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: brandColors.mediumGray,
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Most North American markets are live and additional markets from around the world will be rolling out over the coming months.
            </Typography>
          </Box>

          {/* Continent Grid */}
          <Grid container spacing={3} sx={{ mb: 6, justifyContent: 'center' }}>
            {continentData.map((data, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card
                  onClick={() => handleContinentClick(data.name)}
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${brandColors.lightGray}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: `${brandColors.gold}10`,
                      borderColor: brandColors.gold,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {!data.isActive && (
                    <Chip
                      label="Internal Only"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: '#FFCC00',
                        color: 'black',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={getContinentImagePath(data.name)}
                      alt={data.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        maxWidth: 120,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: brandColors.indigo,
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {data.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: brandColors.mediumGray,
                    }}
                  >
                    {data.count} markets
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Sample Countries Carousel */}
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: brandColors.indigo,
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
              }}
            >
              Popular Countries
            </Typography>
            
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Grid container spacing={2} sx={{ maxWidth: 1000 }}>
                  {popularCountries.map((country, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                      <Card
                        onClick={() => handleCountryClick(country)}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: `${brandColors.blue}10`,
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        <Box
                          className={`fi fi-${country.code}`}
                          sx={{
                            fontSize: 48,
                            mb: 1,
                            display: 'inline-block',
                            lineHeight: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: brandColors.indigo,
                            mb: 1,
                          }}
                        >
                          {country.country}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: brandColors.blue,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            opacity: 0.8,
                            fontSize: '0.75rem',
                          }}
                        >
                          View Markets <ArrowForwardIcon fontSize="small" />
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Box>

          {/* Standalone Sample Report button below the list */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.open('/sample_reports/2Q 2025 MarketCompass Lodging Report - Colorado Springs, CO.pdf', '_blank')}
              sx={{
                borderColor: brandColors.blue,
                color: brandColors.blue,
                px: 4,
                py: 1.5,
                fontSize: '1.125rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderColor: brandColors.blue,
                  backgroundColor: 'rgba(32, 76, 229, 0.05)',
                  borderWidth: 2,
                },
                transition: 'all 0.3s ease',
                minWidth: { xs: 200, sm: 'auto' },
              }}
            >
              View Sample Report
            </Button>
          </Box>
        </Container>
      </Box>

      {/* What's Inside Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              color: brandColors.indigo,
              fontWeight: 600,
              mb: 2,
              textAlign: 'center',
            }}
          >
            What's Inside
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: brandColors.mediumGray,
              mb: 4,
              textAlign: 'center',
              fontSize: '1.125rem',
            }}
          >
            Each report provides comprehensive insights and metrics
          </Typography>
          <Grid container spacing={3}>
            {whatsInsideItems.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 3,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${brandColors.lightGray}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      borderColor: brandColors.gold,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        color: brandColors.blue,
                        backgroundColor: `${brandColors.blue}10`,
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: brandColors.indigo,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: brandColors.mediumGray,
                          lineHeight: 1.6,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Forward View */}
          <Card
            sx={{
              mt: 3,
              p: 3,
              backgroundColor: `${brandColors.gold}10`,
              border: `2px solid ${brandColors.gold}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  color: brandColors.gold,
                  backgroundColor: brandColors.white,
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PsychologyIcon />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: brandColors.indigo,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Forward View
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: brandColors.darkGray,
                    lineHeight: 1.6,
                  }}
                >
                  Interpretation of risks and opportunities in the market, grounded in Horwath HTL's advisory expertise.
                </Typography>
              </Box>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* Customization and Flexibility Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: brandColors.white }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  color: brandColors.indigo,
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Customization and Flexibility
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: brandColors.darkGray,
                  lineHeight: 1.8,
                  fontSize: '1.05rem',
                  mb: 3,
                }}
              >
                MarketCompass allows reports to be built around any geography or property type—from downtown districts and resort corridors to entire metro regions.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: brandColors.darkGray,
                  lineHeight: 1.8,
                  fontSize: '1.05rem',
                  mb: 3,
                }}
              >
                Comp sets can be tailored by:
              </Typography>
              <List>
                {['Brand', 'Class', 'Scale', 'Physical attributes'].map((item, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: brandColors.gold, fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        color: brandColors.darkGray,
                        fontSize: '1rem',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography
                variant="body1"
                sx={{
                  color: brandColors.darkGray,
                  lineHeight: 1.8,
                  fontSize: '1.05rem',
                  mt: 2,
                }}
              >
                This ensures the report reflects the market most relevant to your project.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  border: `1px solid ${brandColors.lightGray}`,
                }}
              >
                <AssessmentIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: brandColors.blue,
                    mb: 2,
                  }} 
                />
                <Typography
                  variant="h5"
                  sx={{
                    color: brandColors.indigo,
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Tailored to Your Needs
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: brandColors.mediumGray,
                    lineHeight: 1.6,
                  }}
                >
                  Every report is customized to match your specific market analysis requirements
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Applications Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              color: brandColors.indigo,
              fontWeight: 600,
              mb: 4,
              textAlign: 'center',
            }}
          >
            Applications
          </Typography>
          <Grid container spacing={3}>
            {applications.map((app, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    textAlign: 'center',
                    backgroundColor: brandColors.white,
                    border: `1px solid ${brandColors.lightGray}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: brandColors.blue,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: brandColors.blue,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {React.cloneElement(app.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: brandColors.indigo,
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {app.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: brandColors.mediumGray,
                      lineHeight: 1.6,
                    }}
                  >
                    {app.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose This Report Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: brandColors.white }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              color: brandColors.indigo,
              fontWeight: 600,
              mb: 2,
              textAlign: 'center',
            }}
          >
            Why Choose This Report
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: brandColors.mediumGray,
              mb: 5,
              textAlign: 'center',
              fontSize: '1.125rem',
            }}
          >
            What makes the Custom Comp Set Report by MarketCompass unique is its combination of:
          </Typography>
          <Grid container spacing={4}>
            {whyChooseReasons.map((reason, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: `${brandColors.gold}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: brandColors.gold,
                    }}
                  >
                    {reason.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: brandColors.indigo,
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      {reason.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: brandColors.darkGray,
                        lineHeight: 1.6,
                      }}
                    >
                      {reason.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* Quote/Testimonial section */}
      <Testimonial 
        quote="Custom Comp Set Reports have proven invaluable when consulting or appraising hotel properties. The tailored analysis and comprehensive benchmarking provide the exact market intelligence needed for informed decision-making."
        title="Managing Director"
        organization="Horwath HTL Consulting"
      />

      {/* FAQ Section for SEO */}
      <FAQSection
        faqs={[
          {
            question: 'What is a Custom Comp Set Report?',
            answer: 'A Custom Comp Set Report is a comprehensive 20+ page market intelligence document that provides detailed hotel performance metrics, competitive set analysis, and market insights tailored to your specific geographic area and property type requirements.',
          },
          {
            question: 'How long does it take to receive a custom report?',
            answer: 'Custom Comp Set Reports are typically delivered within 5-7 business days after defining your specific market parameters and competitive set requirements.',
          },
          {
            question: 'What geographic areas can be covered?',
            answer: 'Reports can be built around any geography—from specific downtown districts and resort corridors to entire metro regions. The flexibility allows for precise market definition based on your project needs.',
          },
          {
            question: 'How far back does the historical data go?',
            answer: 'Our reports include 10+ years of performance history, providing comprehensive context for understanding cyclical and structural market changes.',
          },
          {
            question: 'Can I customize the competitive set?',
            answer: 'Yes, competitive sets can be fully customized by brand, class (Luxury through Economy), scale, physical attributes, or any combination of factors relevant to your analysis.',
          },
          {
            question: 'What makes these reports cost-effective?',
            answer: 'Custom Comp Set Reports deliver the same depth of information as traditional market studies at a fraction of the cost, making professional market intelligence accessible for projects of all sizes.',
          },
        ]}
        title="Custom Comp Set Reports FAQ"
        subtitle="Learn more about our tailored market intelligence solutions"
      />
    </>
  );
};

export default CustomCompSetsPage;